<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useAddVariableTableContentStore } from './useAddVariableTableContentStore';
import { getDisplayNumbers } from '../domains/valueObjects/NumberInput';
import { getDisplayOperator } from '../domains/valueObjects/Operator';
import { getOperators } from '../domains/valueObjects/Operator';
import { getNumberInputs } from '../domains/valueObjects/NumberInput';

import { onUnmounted } from 'vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { TableProperty } from '@/coreDomainsModule/table';

/**
 * props
 * @param useTableHeader - テーブルヘッダ
 */
type Props = {
  tableType: string;
  isEdit?: boolean;
  variableContent?: TableProperty;
};

const props = withDefaults(defineProps<Props>(), {
  isEdit: false,
  variableContent: undefined,
});

const emits = defineEmits<{
  (e: 'onSuccess'): void;
  (e: 'onError'): void;
  (e: 'onDeleteSuccess', variableContentId: number): void;
  (e: 'onDeleteError'): void;
}>();

const inventoryStore = useAddVariableTableContentStore();
const { state } = storeToRefs(inventoryStore);
const {
  initState,
  $reset,
  addNumberInput,
  addOperator,
  addHeader,
  allResetCalculate,
  backCalculate,
  saveCalculate,
  deleteCalculate,
  displayFormula,
} = inventoryStore;

onMounted(() => {
  initState(props.tableType, props.isEdit, props.variableContent);
});

// TODO: baseStoreみたいなの作って、全store継承必須にしてこいつ呼ばせるの強制させたい
onUnmounted(() => {
  $reset();
});

const title = computed({
  /**
   * getter
   */
  get: () => state.value.formulaTitle,
  /**
   * setter
   * @param value - タイトル
   */
  set: (value) => {
    inventoryStore.updateState({
      formulaTitle: value,
    });
  },
});

const isAvailableSave = computed(() => {
  return state.value.formulaTitle !== '' && state.value.isValid && state.value.inputFormula.length !== 0;
});

/**
 * 保存処理
 */
const dispatchSaveCalculate = async () => {
  const res = await saveCalculate();
  if (res.type === 'success') {
    emits('onSuccess');
  } else if (res.type === 'error') {
    emits('onError');
  }
};

/**
 * 削除処理
 */
const dispatchDeleteCalculate = async () => {
  const res = await deleteCalculate(state.value.variableContent?.customFormulaId);
  if (state.value.variableContent?.customFormulaId === undefined) {
    emits('onDeleteError');
    return;
  }
  if (res.type === 'success') {
    emits('onDeleteSuccess', state.value.variableContent.customFormulaId);
  } else if (res.type === 'error') {
    emits('onDeleteError');
  }
};
</script>

<template>
  <div class="add-variable-table-content">
    <div class="add-variable-table-content__calculate-summary calculate-summary">
      <div class="calculate-summary__title">
        <input v-model="title" class="calculate-summary__title--input" :placeholder="'タイトルを入力してください'" />
      </div>
      <div class="calculate-summary__calculate-zone calculate-zone">
        <h3 class="calculate-zone__input-formula">{{ displayFormula(state.inputFormula) }}</h3>
        <h3 v-if="!state.isValid && state.inputFormula.length != 0" class="calculate-zone__input-formula--error">実行不可能な式です</h3>
        <h3 v-else class="calculate-zone__input-formula--empty" />
      </div>
      <div class="calculate-summary__number-inputs number-inputs">
        <div v-for="numberInput in getNumberInputs()" :key="numberInput">
          <h3 class="number-inputs__number" @click="addNumberInput(numberInput)">{{ getDisplayNumbers(numberInput) }}</h3>
        </div>
      </div>
      <div class="calculate-summary__operators operators">
        <div v-for="operator in getOperators()" :key="operator">
          <h3 class="operators__operator" @click="addOperator(operator)">{{ getDisplayOperator(operator) }}</h3>
        </div>
      </div>
    </div>
    <div class="add-variable-table-content__table-headers table-headers">
      <div v-for="header in state.tableContentHeaders" :key="header.headerKey">
        <h3 class="table-headers__header" @click="addHeader(header.headerKey)">{{ header.headerName }}</h3>
      </div>
    </div>
    <div class="add-variable-table-content__button-area button-area">
      <button class="button-area__all-reset" @click="allResetCalculate">やりなおし</button>
      <button class="button-area__back" @click="backCalculate">１つ戻る</button>
      <TextButton color="primary" :disabled="!isAvailableSave" :size="'btn-md'" class="button-area__confirm" @click="dispatchSaveCalculate">
        {{ 'この条件で追加' }}
      </TextButton>
      <TextButton
        v-if="state.isEdit"
        color="primary"
        :button-style="'btn-error'"
        :size="'btn-md'"
        class="button-area__confirm"
        @click="dispatchDeleteCalculate"
      >
        {{ 'このプロパティを削除' }}
      </TextButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.add-variable-table-content {
  display: grid;
  gap: 8px;
  grid-template-columns: minmax(400px, 1fr) 1fr;
  &__calculate-summary {
  }

  &__table-headers {
  }

  &__button-area {
  }
}

.calculate-summary {
  display: grid;
  grid-template-rows: 1fr minmax(0, 72px) 2fr 2fr;
  gap: 8px;
  &__title {
  }

  &__calculate-zone {
  }

  &__number-inputs {
  }

  &__operators {
  }
}

.calculate-summary {
  display: grid;
  grid-template-rows: 1fr minmax(0, 72px) 2fr 2fr;
  gap: 8px;
  &__title {
    display: grid;
    align-items: center;
    padding: 0px 8px;
    border: 1px solid $border;
    border-radius: 8px;
    &--input {
      width: 100%;
      border: none;
      outline: none;
      font-size: 16px;
      padding: 8px;
      border-radius: 8px;
    }
  }

  &__calculate-zone {
    display: grid;
    grid-template-rows: 2fr 1fr;
    gap: 4px;
    &__input-formula {
      display: grid;
      align-items: center;
      padding: 0px 8px;
      border: 1px solid $border;
      border-radius: 8px;
      &--error {
        color: $semanticError;
      }
    }
  }
}

.calculate-zone {
  display: grid;
  grid-template-rows: 2fr 1fr;
  gap: 4px;
  &__input-formula {
    display: grid;
    align-items: center;
    padding: 0px 8px;
    border: 1px solid $border;
    border-radius: 8px;
    &--error {
      color: $semanticError;
    }
  }
}

.table-headers {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border: 1px solid $border;
  border-radius: 8px;
  gap: 8px;
  &__header {
  }
}

.number-inputs {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  border: 1px solid $border;
  border-radius: 8px;
  margin: 0px 0px 16px 0px;
  &__number {
  }
}

.operators {
  display: grid;
  border: 1px solid $border;
  border-radius: 8px;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  &__operator {
  }
}

.button-area {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  &__all-reset {
  }

  &__back {
  }

  &__confirm {
  }
}
</style>
